window.AdminCategories = function AdminCategories({ showNotification }) {
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({ id: 0, name: '', description: '', display_order: 0, status: 'active' });
  const [isEditing, setIsEditing] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  React.useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('categories.php?admin=1');
      if (res.success) setCategories(res.data || []);
    } catch (e) {
      setCategories([
        { id: 1, name: 'Railways', slug: 'railways', display_order: 1, status: 'active' },
        { id: 2, name: 'Industrial Automation', slug: 'industrial-automation', display_order: 2, status: 'active' },
        { id: 3, name: 'Aerospace', slug: 'aerospace', display_order: 3, status: 'active' },
        { id: 4, name: 'Automotive', slug: 'automotive', display_order: 4, status: 'active' },
        { id: 5, name: 'Custom CAD & Fixtures', slug: 'custom-cad-fixtures', display_order: 5, status: 'active' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await window.apiClient.put('categories.php', form);
        showNotification(`Category '${form.name}' updated.`);
      } else {
        await window.apiClient.post('categories.php', form);
        showNotification(`Category '${form.name}' added.`);
      }
      setForm({ id: 0, name: '', description: '', display_order: categories.length + 1, status: 'active' });
      setIsEditing(false);
      fetchCategories();
    } catch (err) {
      showNotification(err.message || 'Operation failed.', 'error');
    }
  };

  const handleEditClick = (cat) => {
    setIsEditing(true);
    setForm({
      id: cat.id,
      name: cat.name,
      description: cat.description || '',
      display_order: cat.display_order || 0,
      status: cat.status || 'active'
    });
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Delete category '${name}'?`)) return;
    try {
      await window.apiClient.delete('categories.php', { id });
      showNotification(`Category '${name}' deleted.`);
      fetchCategories();
    } catch (err) {
      showNotification(err.message || 'Deletion failed.', 'error');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      
      {/* Top Header */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <h1 className="font-serif text-2xl font-bold text-slate-900">Product Category Management</h1>
        <p className="font-sans text-xs text-slate-500">Add, edit, hide, and reorder product categories.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Form Card (5 Cols) */}
        <div className="lg:col-span-5 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 text-slate-800">
          <h3 className="font-serif text-lg font-bold text-slate-900 border-b border-slate-100 pb-3">
            {isEditing ? 'Edit Category' : 'Create New Category'}
          </h3>

          <form onSubmit={handleSaveCategory} className="space-y-4 text-xs">
            <div>
              <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">CATEGORY NAME *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Defense & Robotics"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary"
              />
            </div>

            <div>
              <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">DESCRIPTION</label>
              <textarea
                rows="3"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary"
              ></textarea>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">DISPLAY ORDER</label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary font-mono"
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] font-bold text-slate-600 uppercase mb-1">STATUS</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary"
                >
                  <option value="active">Active</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            </div>

            <div className="pt-2 flex gap-3">
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setForm({ id: 0, name: '', description: '', display_order: 0, status: 'active' });
                  }}
                  className="w-1/2 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-mono py-2.5 rounded font-bold cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className={`${isEditing ? 'w-1/2' : 'w-full'} bg-secondary hover:bg-orange-600 text-white font-mono py-2.5 rounded font-bold uppercase tracking-wider shadow cursor-pointer`}
              >
                {isEditing ? 'Update' : 'Add Category'}
              </button>
            </div>
          </form>
        </div>

        {/* Categories List Table (7 Cols) */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="relative w-full">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
              <input
                type="text"
                placeholder="Search categories by name or slug..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs outline-none focus:border-secondary transition-colors"
              />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-left text-xs font-sans">
              <thead className="bg-slate-50 text-slate-500 font-mono text-[10px] uppercase border-b border-slate-200">
                <tr>
                  <th className="p-4">ORDER</th>
                  <th className="p-4">NAME & SLUG</th>
                  <th className="p-4">STATUS</th>
                  <th className="p-4 text-center">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {categories
                  .filter(cat => {
                    if (!cat) return false;
                    const query = searchQuery.toLowerCase();
                    return (
                      (cat.name || '').toLowerCase().includes(query) ||
                      (cat.slug || '').toLowerCase().includes(query)
                    );
                  })
                  .map((cat) => (
                <tr key={cat.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 font-mono font-bold text-secondary">{cat.display_order}</td>
                  <td className="p-4">
                    <div className="font-bold text-slate-900">{cat.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{cat.slug}</div>
                  </td>
                  <td className="p-4">
                    <span className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded ${
                      cat.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      {cat.status}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button onClick={() => handleEditClick(cat)} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded border border-slate-200 cursor-pointer">
                        <span className="material-symbols-outlined text-sm">edit</span>
                      </button>
                      <button onClick={() => handleDeleteCategory(cat.id, cat.name)} className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200 cursor-pointer">
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {categories.length > 0 && categories.filter(cat => {
                const query = searchQuery.toLowerCase();
                return (
                  (cat.name || '').toLowerCase().includes(query) ||
                  (cat.slug || '').toLowerCase().includes(query)
                );
              }).length === 0 && (
                <tr>
                  <td colSpan="4" className="p-8 text-center text-slate-400 font-mono">No categories match your search.</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

      </div>

    </div>
  );
};
